"use client";

import React, { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { CarouselSectionData } from "@/themes/default/templates/section/types";

/**
 * dental06 "clinical" hero slider (`section.variant === "clinical"`).
 *
 * Built for WIDE campus photography (the UDM shots are ~3:1 panoramas):
 *   - the photo spans the full width at a height that suits wide frames
 *     (clamp 440–620px ≈ 40vw), only a light bottom gradient — the picture
 *     is not dimmed into a backdrop;
 *   - the copy sits in a white frosted-glass card bottom-left (navy title,
 *     amber eyebrow, buttons) — unlike the centred white-on-dark text of the
 *     um1sf / epona / ci926 sliders;
 *   - a thumbnail rail bottom-right picks slides (active thumb ringed amber,
 *     autoplay progress under it);
 *   - the height is FIXED (card is absolutely placed), so slides with more or
 *     less copy never move the page below;
 * Mobile: photo on top, the card below as a normal block, dots instead of
 * thumbnails.
 */

type Lang = "en" | "mm";
const pick = (t: any, lang: Lang): string =>
  !t ? "" : typeof t === "string" ? t : t[lang] || t.en || t.mm || "";

export function ClinicalCarousel({
  section,
  currentLanguage = "en",
}: {
  section: CarouselSectionData;
  currentLanguage?: Lang;
}) {
  const slides = (section.slides || []) as any[];
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const speed = section.autoplaySpeed ?? 7000;
  const autoplay = (section.autoplay ?? true) && slides.length > 1;

  const go = useCallback(
    (i: number) => setActive(((i % slides.length) + slides.length) % slides.length),
    [slides.length],
  );

  useEffect(() => {
    if (!autoplay || paused) return;
    const t = setTimeout(() => go(active + 1), speed);
    return () => clearTimeout(t);
  }, [active, autoplay, paused, speed, go]);

  if (!slides.length) return null;
  const s = slides[active];
  const title = pick(s.title, currentLanguage);
  const eyebrow = pick(s.subtitle, currentLanguage);
  const description = pick(s.description, currentLanguage);
  const buttons: any[] = Array.isArray(s.buttons) ? s.buttons : [];

  const Copy = (
    <div key={active} className="dental06-clinical-copy">
      {eyebrow && (
        <p className="mb-2 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--dental06-blue)" }}>
          <span className="h-[3px] w-6 rounded-full" style={{ backgroundColor: "var(--color-accent)" }} />
          <span className="line-clamp-1">{eyebrow}</span>
        </p>
      )}
      {title && (
        <h2 className="dental06-hero-title line-clamp-2 text-2xl leading-tight sm:text-3xl xl:text-[2.4rem]" style={{ color: "var(--dental06-navy)" }}>
          {title}
        </h2>
      )}
      {description && (
        <p className="mt-3 line-clamp-2 text-[15px] font-medium leading-relaxed" style={{ color: "color-mix(in srgb, var(--dental06-navy) 82%, transparent)" }}>{description}</p>
      )}
      {buttons.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-3">
          {buttons.map((b, i) => {
            const primary = b.style === "primary";
            return (
              <Link
                key={i}
                href={b.url || "#"}
                target={b.openInNewTab ? "_blank" : undefined}
                className={`inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold transition ${
                  primary
                    ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-foreground)] hover:brightness-95"
                    : "border text-[color:var(--dental06-navy)] hover:bg-[color:var(--dental06-navy-tint)]"
                }`}
                style={primary ? undefined : { borderColor: "var(--color-border)" }}
              >
                {pick(b.text, currentLanguage)}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <section
      className="relative w-full"
      data-section-id={(section as any)._id}
      data-section-type="carousel"
      data-variant="clinical"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label={pick((section as any).title, currentLanguage) || "Highlights"}
    >
      <div className="relative h-56 overflow-hidden sm:h-72 lg:h-[clamp(440px,40vw,620px)]">
        {slides.map((sl, i) => (
          <div
            key={sl.id || i}
            className="absolute inset-0 transition-opacity duration-[900ms] ease-out"
            style={{ opacity: i === active ? 1 : 0 }}
            aria-hidden={i !== active}
          >
            {sl.backgroundImage && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={sl.backgroundImage}
                alt={pick(sl.title, currentLanguage)}
                className="h-full w-full object-cover"
                style={{
                  transform: i === active ? "scale(1)" : "scale(1.05)",
                  transition: `transform ${speed + 1500}ms ease-out`,
                }}
                loading={i === 0 ? "eager" : "lazy"}
              />
            )}
          </div>
        ))}
        {/* light bottom gradient only — the photo stays the picture */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2"
          style={{ background: "linear-gradient(0deg, rgba(13,33,51,0.55) 0%, transparent 100%)" }}
        />

        {/* desktop overlay: glass card (left) + thumbnail rail (right) */}
        <div className="absolute inset-x-0 bottom-0 hidden lg:block">
          <div className="mx-auto flex max-w-7xl items-end justify-between gap-8 px-8 pb-10">
            <div
              className="w-full max-w-xl rounded-2xl border p-7"
              style={{
                // Lighter glass: more of the photo shows through; the stronger
                // blur + saturation keeps the navy copy readable.
                backgroundColor: "rgba(255,255,255,0.55)",
                backdropFilter: "blur(20px) saturate(150%)",
                WebkitBackdropFilter: "blur(20px) saturate(150%)",
                borderColor: "rgba(255,255,255,0.55)",
                boxShadow: "0 30px 60px -30px rgba(11,58,94,0.55)",
              }}
            >
              {Copy}
            </div>

            {slides.length > 1 && (
              <div className="flex shrink-0 items-end gap-2.5" role="tablist" aria-label="Slides">
                <button
                  type="button"
                  onClick={() => go(active - 1)}
                  className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[color:var(--dental06-navy)] shadow-md transition hover:bg-[color:var(--color-accent)]"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {slides.map((sl, i) => {
                  const on = i === active;
                  return (
                    <button
                      key={sl.id || i}
                      type="button"
                      role="tab"
                      aria-selected={on}
                      aria-label={pick(sl.title, currentLanguage)}
                      title={pick(sl.title, currentLanguage)}
                      onClick={() => go(i)}
                      className="group relative overflow-hidden rounded-lg transition-all"
                      style={{
                        width: on ? 92 : 64,
                        height: on ? 62 : 44,
                        boxShadow: on ? "0 0 0 3px var(--color-accent), 0 12px 24px -10px rgba(0,0,0,0.6)" : "0 0 0 2px rgba(255,255,255,0.75)",
                        opacity: on ? 1 : 0.8,
                      }}
                    >
                      {sl.backgroundImage && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={sl.backgroundImage} alt="" className="h-full w-full object-cover" loading="lazy" />
                      )}
                      <span className="absolute left-1 top-1 rounded bg-black/55 px-1 text-[10px] font-bold tabular-nums text-white">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {on && autoplay && !paused && (
                        <span
                          key={`p${active}`}
                          className="absolute bottom-0 left-0 h-[3px] w-full origin-left"
                          style={{ background: "var(--color-accent)", animation: `dental06-slide-progress ${speed}ms linear forwards` }}
                        />
                      )}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => go(active + 1)}
                  className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[color:var(--dental06-navy)] shadow-md transition hover:bg-[color:var(--color-accent)]"
                  aria-label="Next slide"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* mobile: card below the photo + dots */}
      <div className="px-4 pb-14 pt-6 sm:px-6 lg:hidden" style={{ backgroundColor: "var(--dental06-mist)" }}>
        <div className="min-h-[9.5rem]">{Copy}</div>
        {slides.length > 1 && (
          <div className="mt-5 flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => go(i)}
                aria-label={`Slide ${i + 1}`}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === active ? 28 : 10,
                  backgroundColor: i === active ? "var(--color-accent)" : "color-mix(in srgb, var(--dental06-navy) 25%, transparent)",
                }}
              />
            ))}
          </div>
        )}
      </div>
      <div aria-hidden className="dental06-crest-stripe" />
    </section>
  );
}

export default ClinicalCarousel;
